// Transcript state for a course card (ACC-355): the status tag near the title
// and the "N days left to add to transcript" sub-text. Kept as a pure function
// so it's testable and the deadline is computed in exactly one place.

// Figma 1250-9209 / 9237 / 9263, 1252-13233. Colours live in ui/Badge.vue
// (subtle tone) — this module only picks the semantic variant.
const TAGS = {
  added: { text: 'ADDED TO TRANSCRIPT', variant: 'success', icon: 'mdi-check' },
  ready: { text: 'READY TO ADD TO TRANSCRIPT', variant: 'warning', icon: null },
  approaching: { text: 'DEADLINE APPROACHING', variant: 'info', icon: null },
  expired: { text: 'EXPIRED', variant: 'default', icon: null },
  unenrolled: { text: 'UNENROLLED', variant: 'default', icon: null }
};

// Days out at which a course flips from READY to DEADLINE APPROACHING. The
// countdown sub-text only shows inside this window.
export const DEADLINE_APPROACHING_DAYS = 60;

const lc = (v) => (v || '').toString().toLowerCase();

// Whole days until the transcript deadline (course completion + 1 year).
// null when the completion date is missing or unparsable.
export function transcriptDaysLeft(courseCompletedDate, now = Date.now()) {
  if (!courseCompletedDate) return null;
  const deadline = new Date(courseCompletedDate);
  if (isNaN(deadline.getTime())) return null;
  deadline.setFullYear(deadline.getFullYear() + 1);
  return Math.ceil((deadline.getTime() - now) / 86400000);
}

export function getTranscriptState(
  {
    buttonLabel,
    buttonAction,
    courseSection,
    courseCompletedDate,
    transcriptedDate,
    creditConverted,
    creditEligible,
    enrollmentStatus,
    creditWindowOverride
  },
  now = Date.now()
) {
  const daysLeft = transcriptDaysLeft(courseCompletedDate, now);
  // ULC-3378: an approved override suspends the deadline, so no countdown and no
  // EXPIRED tag while the course is still addable.
  const overridden = creditWindowOverride === true;

  const isAdded =
    buttonAction === 'View Transcript' ||
    buttonLabel === 'View Transcript' ||
    courseSection === 'Transcripted Courses' ||
    (lc(creditConverted) === 'y' && !!transcriptedDate);

  // 'Past Courses' wins over the button label: those courses already fell out of
  // the add-to-transcript window, even if the backend still labels the button.
  const isPastSection = courseSection === 'Past Courses';
  const isAddFlow = !isPastSection && (buttonLabel === 'Add to Transcript' || courseSection === 'Add to Transcript');

  let tag = null;
  if (isPastSection && lc(enrollmentStatus) === 'inactive') {
    // Learner dropped it — distinct from letting the transcript window lapse.
    tag = TAGS.unenrolled;
  } else if (isAdded) {
    tag = TAGS.added;
  } else if (isAddFlow && courseCompletedDate) {
    // Never EXPIRED here: the app's real cutoff is completion + 1yr + 1 day, so a
    // course still in the add-to-transcript bucket is still addable (the button is
    // showing). On its final day daysLeft hits 0 — that's urgent, not expired.
    // Once the window truly closes the course moves to Past Courses instead.
    if (daysLeft === null) tag = null;
    else if (overridden) tag = TAGS.ready;
    else if (daysLeft <= DEADLINE_APPROACHING_DAYS) tag = TAGS.approaching;
    else tag = TAGS.ready;
  } else if (
    isPastSection ||
    (!overridden && lc(creditEligible) === 'y' && lc(creditConverted) !== 'y' && daysLeft !== null && daysLeft <= 0)
  ) {
    // Everything left in Past Courses is expired, whether the learner missed the
    // credit window or simply never finished — the section is uniform (ULC-3265).
    tag = TAGS.expired;
  }

  // Countdown only while the deadline is actually approaching (<= 60 days), and
  // never on an already-added course — that would contradict the ADDED tag.
  // (Added + add-section happens when the backend labels the button
  // "View Transcript", e.g. credit-converted retries.)
  let daysLeftText = '';
  if (tag === TAGS.approaching) {
    daysLeftText =
      daysLeft > 0
        ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} left to add to transcript`
        : 'Last day to add to transcript';
  }

  return { tag, daysLeft, daysLeftText };
}

// Course codes among `Add to Transcript` courses whose deadline is approaching.
// Feeds the transcript-deadline banner; reuses the state logic above so the
// 60-day threshold lives in exactly one place.
export function deadlineApproachingCourseCodes(toAddCourses, now = Date.now()) {
  return (toAddCourses || [])
    .filter(
      (c) =>
        getTranscriptState(
          {
            courseSection: 'Add to Transcript',
            courseCompletedDate: c._courseCompletedDate,
            creditEligible: c._creditEligible,
            creditConverted: c._creditConverted,
            transcriptedDate: c._transcriptedDate,
            creditWindowOverride: c._creditWindowOverride
          },
          now
        ).tag === TAGS.approaching
    )
    .map((c) => c._courseInfo?._coursePrefixDisplayLabel || c._courseInfo?._coursePrefix)
    .filter(Boolean);
}
